import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  tap,
  zip,
} from 'rxjs';

import { environment } from '../../../environments/environment';
import { UnitsOfMeasureToListItems } from '../../modules/accounts/utils';
import {
  EntityType,
  IEntity,
  ILookupEntity,
} from '../../modules/entities/dtos/entities';
import { SustainableDevelopmentGoal } from '../../modules/projects/dtos/project';
import { Category } from '../dtos/category.dto';
import { AccountType, IUnitOfMeasure, ReferenceData } from '../dtos';
import { IUnitOfMeasureConversion } from '../dtos/unit-of-measure-conversion';
import { EnumHelper, ListItem, SystemParameter } from '@canvas/four-r-digital';
import { IUserGroup } from '../dtos/user-group';
import { GenderType } from '../dtos/gender-type';
import { ConfigSettings } from '../dtos/config-settings';

@Injectable({
  providedIn: 'root',
})
export class ReferenceDataService {
  // TODO use EnumHelper.enumToList for these!!!
  referenceData = {
    accountTypes: [
      { id: AccountType.Control, text: 'Control' } as ListItem,
      { id: AccountType.Collection, text: 'Collection' } as ListItem,
      { id: AccountType.Loan, text: 'Loan' } as ListItem,
      { id: AccountType.Buffer, text: 'Buffer' } as ListItem,
      { id: AccountType.Unidentified, text: 'Unidentified' } as ListItem,
    ],
    accountOwnerTypes: [
      { id: 1, text: 'Service Provider' } as ListItem,
      { id: 2, text: 'Organisation' } as ListItem,
      { id: 3, text: 'Customer' } as ListItem,
      { id: 4, text: 'Asset' } as ListItem,
      { id: 5, text: 'Asset Owner' } as ListItem,
      { id: 6, text: 'Duka' } as ListItem,
      { id: 7, text: 'Project' } as ListItem,
      { id: 8, text: 'Entity' } as ListItem,
      { id: 9, text: 'Participant' } as ListItem,
    ],
    deviceStatuses: [
      { id: 1, text: 'Active' },
      { id: 2, text: 'Stopped' },
      { id: 4, text: 'InActive' },
    ],
    accountBalanceDirections: [
      { id: 1, text: 'Both' },
      { id: 2, text: 'Positive' },
      { id: 3, text: 'Negative' },
    ],
    accountStatuses: [
      { id: 1, text: 'Active' },
      { id: 2, text: 'Closed' },
      { id: 3, text: 'Deleted' },
    ],
    billingRuleType: [
      { id: 1, text: 'Variable' },
      { id: 2, text: 'Flat' },
    ],
    billingPeriod: [
      { id: 1, text: 'Daily' },
      { id: 2, text: 'Weekly' },
      { id: 3, text: 'Monthly' },
    ],
    entityType: [
      { id: 0, text: 'All' },
      { id: 100, text: 'Supply Partner' },
      { id: 200, text: 'Service Provider' },
      { id: 300, text: 'Buyer' },
      { id: 400, text: 'Participant' },
      { id: 500, text: 'Community' },
    ],
    genders: EnumHelper.enumToList(GenderType),
  } as ReferenceData;
  private _hasInitialised = false;
  private _unitsOfMeaure: IUnitOfMeasure[] = [];
  private _unitOfMeaureListItems: ListItem[] = [];
  private _projectBenefits: SustainableDevelopmentGoal[] | undefined =
    undefined;
  private _projectBenefitsObservable:
    | Observable<SustainableDevelopmentGoal[]>
    | undefined = undefined;
  private segmentationSDGs = new BehaviorSubject<
    SustainableDevelopmentGoal[] | null
  >(null);
  private _userGroupListItem: ListItem[] = [];
  private isGettingUser = false;
  private _referenceDataSub: Subject<ReferenceData> = new BehaviorSubject(
    this.referenceData
  );
  private _configSettings: ConfigSettings | undefined = undefined;
  private configSettingsSub = new BehaviorSubject<ConfigSettings | null>(
    null
  );

  segmentationSDGs$ = this.segmentationSDGs.pipe(filter((x) => x != null));
  configSettings$ = this.configSettingsSub.pipe(
    filter((x): x is ConfigSettings => x != null)
  );

  constructor(private httpClient: HttpClient) {
    this.initialise();
    this.loadConfigSettings();
  }

  private _entitiesListItems: ListItem[] = [];

  get entitiesListItems() {
    return this._entitiesListItems;
  }

  private _user: { userName: string } | undefined = undefined;

  get user(): { userName: string } | undefined {
    if (!this._user && !this.isGettingUser) {
      this.isGettingUser = true;
      this.httpClient
        .get(`${environment.baseApiUrl}referencedata/user`)
        .subscribe({
          next: (user) => {
            this._user = user as { userName: string };
            this.isGettingUser = false;
          },
          error: () => {
            this.isGettingUser = false;
          },
        });
    }
    return this._user;
  }

  private _systemParameters: Record<string, unknown> = {};
  private _pendingSystemParameters: Record<string, Observable<unknown>> = {};

  get systemParameters() {
    return this._systemParameters;
  }

  get unitsOfMeasure() {
    return this._unitsOfMeaure;
  }

  get unitOfMeasureListItems() {
    if (this._unitOfMeaureListItems.length === 0) {
      this._unitOfMeaureListItems = UnitsOfMeasureToListItems(
        this._unitsOfMeaure
      );
    }

    return this._unitOfMeaureListItems;
  }

  get isLoaded() {
    return this.unitOfMeasureListItems.length > 0;
  }

  get userGroupItem() {
    return this._userGroupListItem;
  }

  get configSettings(): ConfigSettings | undefined {
    return this._configSettings;
  }

  private loadConfigSettings(): void {
    this.httpClient
      .get<ConfigSettings>(`${environment.baseApiUrl}referencedata/config-settings`)
      .subscribe((settings) => {
        this._configSettings = settings;
        this.configSettingsSub.next(settings);
      });
  }

  initialise(reInitialize = true) {
    if (this._hasInitialised && reInitialize) {
      return;
    }

    const all = zip(
      this.getUnitsOfMeasure(),
      this.getCategories(),
      this.getUserGroups()
    );

    all.subscribe(([unitsOfMeasure, categories, userGroups]) => {
      this._unitsOfMeaure = unitsOfMeasure as IUnitOfMeasure[];
      this._userGroupListItem = userGroups;
      this.referenceData.categories = categories;
      this.referenceData.unitsOfMesaure = unitsOfMeasure;
      this._referenceDataSub.next(this.referenceData);
    });
    this._hasInitialised = true;
  }

  loadSegmentationSDGs() {
    this.httpClient
      .get<SustainableDevelopmentGoal[]>(
        `${environment.baseApiUrl}project/segmentation-sdgs`
      )
      .subscribe((res) => {
        this.segmentationSDGs.next(res);
      });
  }

  getReferenceData(): Observable<ReferenceData> {
    return this._referenceDataSub;
  }

  getCategories() {
    return this.httpClient
      .get<Category[]>(`${environment.baseApiUrl}category`)
      .pipe(
        map((categories) => {
          return categories.map((c) => ({ id: c.id, text: c.name }));
        })
      );
  }

  getUnitsOfMeasure() {
    return this.httpClient.get<IUnitOfMeasure[]>(
      `${environment.baseApiUrl}units/list`
    );
  }

  getUserGroups() {
    return this.httpClient.get<IUserGroup[]>(`/api/UserGroup/list`).pipe(
      map((grp) => {
        return grp.map((c) => ({ id: c.id, text: c.name }));
      })
    );
  }

  getUnitsOfMeasureConversions() {
    return this.httpClient.get<IUnitOfMeasureConversion[]>(
      `${environment.baseApiUrl}units/conversions`
    );
  }

  getProjectBenefits() {
    if (this._projectBenefits !== undefined) {
      this._projectBenefitsObservable = undefined;
      return of(this._projectBenefits);
    }
    if (this._projectBenefitsObservable !== undefined) {
      return this._projectBenefitsObservable;
    } else {
      this._projectBenefitsObservable = this.httpClient
        .get<SustainableDevelopmentGoal[]>(
          `${environment.baseApiUrl}project/project-benefits`
        )
        .pipe(
          map((res) => {
            this._projectBenefits = res;
            return res;
          })
        );
      return this._projectBenefitsObservable;
    }
  }

  getSystemParameter(parameter: SystemParameter) {
    if (parameter in this._systemParameters) {
      return of(this._systemParameters[parameter]);
    }
    const pendingRequest = this._pendingSystemParameters[parameter];
    if (pendingRequest) {
      return pendingRequest;
    }
    const request = this.httpClient
      .get(`${environment.baseApiUrl}referenceData/${parameter}`)
      .pipe(
        tap((data) => {
          this._systemParameters[parameter] = data;
        }),
        finalize(() => delete this._pendingSystemParameters[parameter]),
        catchError((error) => {
          throw 'Error getting system parameter ' + error;
        }),
        shareReplay(1)
      );
    this._pendingSystemParameters[parameter] = request;
    return request;
  }

  getEntitiesByType(
    entityType: EntityType = EntityType.All
  ): Observable<ListItem[]> {
    return this.httpClient
      .get<IEntity[]>(`${environment.baseApiUrl}entity/byType/${entityType}`)
      .pipe(
        map((entities) => {
          const listItems = entities.map((entity) => ({
            id: entity.uniqueIdentifier as any,
            text: entity.name,
          }));
          this._entitiesListItems = listItems;
          return listItems;
        })
      );
  }
  getAvailableParticipantsLookup(projectId?: number, term?: string) {
    let params = new HttpParams();
    if (term) {
      params = params.set('term', term);
    }
    if (projectId) {
      params = params.set('projectId', projectId);
    }
    return this.httpClient.get<ILookupEntity[]>(
      `${environment.baseApiUrl}entity/available-participants-lookup`,
      { params: params }
    );
  }
}
